import {
  Button,
  Cell,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  MuiDialog,
  Typography,
} from '@pickupp/ui/core'
import { makeStyles } from '@pickupp/ui/styles'
import React, { useContext, useEffect, useState } from 'react'

import { MODE } from '~/config'
import { GeolocationContext, LocaleContext } from '~/src/contexts'
import { useTranslation } from '~/src/hooks'

import styles from './styles'

const useStyles = makeStyles(styles)

const PLACEHOLDER_VARIABLES = ['VISITED_REGION', 'CURRENT_REGION']
const COUNTRY_NAME = {
  HK: {
    en: 'Hong Kong',
    ms: 'Hong Kong',
    zh: '香港',
  },
  SG: {
    en: 'Singapore',
    ms: 'Singapore',
    zh: '新加坡',
  },
  MY: {
    en: 'Malaysia',
    ms: 'Malaysia',
    zh: '馬來西亞',
  },
  TW: {
    en: 'Taiwan',
    ms: 'Taiwan',
    zh: '台灣',
  },
}

const RegionDialog = () => {
  const classes = useStyles()
  const t = useTranslation()

  const title = t('region.dialog.title')
  const contentText = t('region.dialog.content')
  const stayButtonText = t('region.dialog.stay.button')
  const leaveButtonText = t('region.dialog.leave.button')

  const { region: visitedRegion, language } = useContext(LocaleContext)
  const { country: currentRegion, updateCountry } = useContext(GeolocationContext)
  const [open, setOpen] = useState(false)
  const [dialog, setDialog] = useState({
    title,
    contentText,
    stayButtonText,
    leaveButtonText,
  })

  const regionsName = {
    VISITED_REGION: COUNTRY_NAME[visitedRegion] ? COUNTRY_NAME[visitedRegion][language] : '',
    CURRENT_REGION: COUNTRY_NAME[currentRegion] ? COUNTRY_NAME[currentRegion][language] : '',
  }

  useEffect(() => {
    //* GeolocationContext might be have delay on, and we do not wish to show empty "country" before it is loaded.
    if (currentRegion && COUNTRY_NAME[currentRegion]) {
      setOpen(visitedRegion !== currentRegion)
    } else {
      //* NOTE: Following are the scenarios that RegionDialog will be disabled.
      //* 1) User is visiting from country out of coverage (eg. Vietnam, Indonesia)
      //* 2) Otherwise, somehow server didn't response properly with header of "cloudfront-viewer-country".
      setOpen(false)
    }
  }, [currentRegion, visitedRegion])

  const substitute = (text, value) => {
    return (PLACEHOLDER_VARIABLES.reduce((result, current) => {
      const pattern = `{${current}}`
      const regex = new RegExp(pattern, 'g')
      return result.replace(regex, value[current])
    }, text))
  }

  useEffect(() => {
    setDialog((prevDialog) => ({
      ...prevDialog,
      contentText: substitute(contentText, regionsName),
      stayButtonText: substitute(stayButtonText, regionsName),
      leaveButtonText: substitute(leaveButtonText, regionsName),
    }))
  }, [visitedRegion, currentRegion, language])

  const closeDialog = () => {
    setOpen(false)
  }

  const onSwitchRegion = (selectedRegion) => {
    closeDialog()
    updateCountry(selectedRegion)
    if (selectedRegion !== visitedRegion) {
      //* Redirect to the correct region
      if (MODE === 'production') {
        window.location.host = `${selectedRegion.toLowerCase().trim()}.pickupp.io`
      } else {
        //* Non-production mode dont have other region. So, simply reload the page to simulate.
        window.location.reload()
      }
    }
  }

  return (
    <MuiDialog
      open={open}
      //* If user close the dialog without choosing region, the dialog will still prompt on refresh.
      onClose={closeDialog}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {dialog.title}
        <Icon
          name="close"
          onClick={closeDialog}
          className={classes.dialogCloseButton}
        />
      </DialogTitle>
      <DialogContent>
        <Grid spacing={2}>
          <Cell xs={12}>
            <Typography>
              {dialog.contentText}
            </Typography>
          </Cell>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Grid spacing={2}>
          <Cell xs={12}>
            <Button
              className={classes.dialogButton}
              onClick={() => { onSwitchRegion(currentRegion) }}
            >
              {
                //* Leave to the correct region.
                dialog.leaveButtonText
              }
            </Button>
          </Cell>
          <Cell xs={12}>
            <Button
              className={classes.dialogButton}
              variant="outlined"
              onClick={() => { onSwitchRegion(visitedRegion) }}
            >
              {
                //* Stay on this region.
                dialog.stayButtonText
              }
            </Button>
          </Cell>
        </Grid>
      </DialogActions>
    </MuiDialog>
  )
}

export default RegionDialog
